import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { LayoutDashboard, Plus, Users, Phone, Package, Menu, Settings, Activity, MessageSquare, X, LogOut, User, ChevronDown, BookOpen, TrendingUp, Search, Calendar, Bell, Wine, ShoppingCart, BarChart3, Warehouse, Building2, Truck, Shield, UserCog } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

import { AdminAlertsDropdown } from './AdminAlertsDropdown';
import Icon from '../AppIcon';
import ThemeToggle from './ThemeToggle';


export default function AdminNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isBarrelMenuOpen, setIsBarrelMenuOpen] = useState(false);
  const [isProClientsMenuOpen, setIsProClientsMenuOpen] = useState(false);
  const [isAdministrationMenuOpen, setIsAdministrationMenuOpen] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const { isSuperAdmin, userProfile } = useAuth();

  // Fetch the real authenticated user from Supabase on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user?.email) {
          setCurrentUserEmail(user?.email);
        }
      } catch (err) {
        console.error('Failed to load user:', err);
      }
    };
    loadUser();

    // Also listen for auth state changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setCurrentUserEmail(session?.user?.email || '');
    });

    return () => subscription?.unsubscribe();
  }, []);

  const navigationItems = [
    { 
      name: 'Notifications', 
      path: '/admin-notifications', 
      icon: Phone 
    },
    { name: 'Tableau de bord', path: '/admin-dashboard', icon: LayoutDashboard },
    { name: 'Nouvel envoi', path: '/create-new-shipment', icon: Plus },
    { name: 'Clients', path: '/admin-clients', icon: Users }
  ];

  const isActive = (path) => location?.pathname === path;

  const activeClass = 'bg-green-600 text-white';
  const inactiveClass = 'text-gray-800 hover:text-green-600 hover:bg-green-50';

  const linkClasses = (path) => {
    const active = isActive(path);
    return `flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'text-primary bg-primary/10 dark:bg-primary/20' :'text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-50 dark:hover:bg-slate-700'
    }`;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      // 1. Clear all stored auth data
      localStorage.removeItem('adtAdminAuth');
      sessionStorage.removeItem('adtAdminAuth');
      
      // 2. Sign out from Supabase
      await supabase?.auth?.signOut();
      
      // 3. Navigate to admin login
      navigate('/admin-login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if supabase signOut fails, still clear local data and navigate
      localStorage.removeItem('adtAdminAuth');
      sessionStorage.removeItem('adtAdminAuth');
      navigate('/admin-login', { replace: true });
    }
  };

  // Extract page title from current route
  const getPageTitle = () => {
    const path = location?.pathname;
    if (path?.includes('shipments')) return 'Envois';
    if (path?.includes('containers')) return 'Conteneurs';
    if (path?.includes('distribution')) return 'Distribution Abidjan';
    if (path?.includes('clients')) return 'Clients';
    if (path?.includes('employees')) return 'Employés';
    if (path?.includes('pickup')) return 'Collectes';
    if (path?.includes('dashboard')) return 'Tableau de bord';
    return 'Admin';
  };

  // Navigation items for mobile drawer
  const mobileNavigationItems = [
    { 
      name: 'Tableau de bord', 
      path: '/admin-dashboard',
      icon: LayoutDashboard,
      isActive: location?.pathname === '/admin-dashboard',
      onClick: () => navigate('/admin-dashboard')
    },
    { 
      name: 'Envois', 
      path: '/admin-shipments-dashboard',
      icon: Package,
      isActive: location?.pathname === '/admin-shipments-dashboard',
      onClick: () => navigate('/admin-shipments-dashboard')
    },
    { 
      name: 'Conteneurs', 
      path: '/admin-containers-dashboard',
      icon: Package,
      isActive: location?.pathname === '/admin-containers-dashboard',
      onClick: () => navigate('/admin-containers-dashboard')
    },
    { 
      name: 'Distribution Abidjan', 
      path: '/abidjan-container-distribution-dashboard',
      icon: Package,
      isActive: location?.pathname === '/abidjan-container-distribution-dashboard',
      onClick: () => navigate('/abidjan-container-distribution-dashboard')
    },
    { 
      name: 'Clients', 
      path: '/admin-clients',
      icon: Users,
      isActive: location?.pathname === '/admin-clients',
      onClick: () => navigate('/admin-clients')
    },
    { 
      name: 'Clients Pro', 
      path: '/professional-clients-management-dashboard',
      icon: Building2,
      isActive: location?.pathname === '/professional-clients-management-dashboard',
      onClick: () => navigate('/professional-clients-management-dashboard')
    },
    { 
      name: 'Employés', 
      path: '/employee-management-dashboard',
      icon: Users,
      isActive: location?.pathname === '/employee-management-dashboard',
      onClick: () => navigate('/employee-management-dashboard')
    },
    { 
      name: 'Collectes', 
      path: '/pickup-task-planning-system',
      icon: Package,
      isActive: location?.pathname === '/pickup-task-planning-system',
      onClick: () => navigate('/pickup-task-planning-system')
    },
    { 
      name: 'Espace Fatou IA', 
      path: '/fatou-ai-reservation-capture-system',
      icon: Calendar,
      isActive: location?.pathname === '/fatou-ai-reservation-capture-system',
      onClick: () => navigate('/fatou-ai-reservation-capture-system')
    },
    { 
      name: '--- GESTION BARRIQUES & CARTONS ---', 
      path: null,
      icon: Wine,
      isActive: false,
      onClick: null,
      isHeader: true
    },
    { 
      name: 'Vente Barriques & Cartons', 
      path: '/unified-sales-management',
      icon: ShoppingCart,
      isActive: location?.pathname === '/unified-sales-management',
      onClick: () => navigate('/unified-sales-management')
    },
    { 
      name: 'Planning Dépôts', 
      path: '/barrel-deposit-scheduling-system',
      icon: Calendar,
      isActive: location?.pathname === '/barrel-deposit-scheduling-system',
      onClick: () => navigate('/barrel-deposit-scheduling-system')
    },
    { 
      name: 'Commandes Clients',
      path: '/barrel-customer-orders-management',
      icon: Package,
      isActive: location?.pathname === '/barrel-customer-orders-management',
      onClick: () => navigate('/barrel-customer-orders-management')
    },
    { 
      name: 'Commandes Promo Été 2026',
      path: '/promo-summer-orders',
      icon: Wine,
      isActive: location?.pathname === '/promo-summer-orders',
      onClick: () => navigate('/promo-summer-orders')
    },
    { 
      name: 'Inventaire Centralisé',
      path: '/unified-inventory-management',
      icon: Warehouse,
      isActive: location?.pathname === '/unified-inventory-management',
      onClick: () => navigate('/unified-inventory-management')
    },
    { 
      name: 'Analytiques Stock',
      path: '/unified-stock-analytics',
      icon: BarChart3,
      isActive: location?.pathname === '/unified-stock-analytics',
      onClick: () => navigate('/unified-stock-analytics')
    },
    { 
      name: 'Réapprovisionnement',
      path: '/unified-replenishment-center',
      icon: TrendingUp,
      isActive: location?.pathname === '/unified-replenishment-center',
      onClick: () => navigate('/unified-replenishment-center')
    },
    { 
      name: 'Fournisseurs',
      path: '/carton-suppliers-management',
      icon: Truck,
      isActive: location?.pathname === '/carton-suppliers-management',
      onClick: () => navigate('/carton-suppliers-management')
    },
    { 
      name: 'Comptabilité Partenaires',
      path: '/partner-accounting',
      icon: TrendingUp,
      isActive: location?.pathname === '/partner-accounting',
      onClick: () => navigate('/partner-accounting')
    },
    { 
      name: 'Chargement Camions',
      path: '/truck-loading-management',
      icon: Truck,
      isActive: location?.pathname === '/truck-loading-management',
      onClick: () => navigate('/truck-loading-management')
    }
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-40 shadow-sm">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/admin-dashboard" className="flex items-center space-x-2">
                <Package className="h-8 w-8 text-green-600 dark:text-green-500" />
                <span className="text-xl font-bold text-gray-900 dark:text-white">ADT Admin</span>
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-1">
              <Link
                to="/admin-dashboard"
                className={linkClasses('/admin-dashboard')}
              >
                <LayoutDashboard className="h-4 w-4" />
                <span>Tableau de bord</span>
              </Link>

              <Link
                to="/admin-shipments-dashboard"
                className={linkClasses('/admin-shipments-dashboard')}
              >
                <Package className="h-4 w-4" />
                <span>Envois</span>
              </Link>

              <Link
                to="/admin-containers-dashboard"
                className={linkClasses('/admin-containers-dashboard')}
              >
                <Package className="h-4 w-4" />
                <span>Conteneurs</span>
              </Link>

              <Link
                to="/admin-clients"
                className={linkClasses('/admin-clients')}
              >
                <Users className="h-4 w-4" />
                <span>Clients</span>
              </Link>

              <Link
                to="/employee-management-dashboard"
                className={linkClasses('/employee-management-dashboard')}
              >
                <Users className="h-4 w-4" />
                <span>Employés</span>
              </Link>

              <Link
                to="/pickup-task-planning-system"
                className={linkClasses('/pickup-task-planning-system')}
              >
                <Package className="h-4 w-4" />
                <span>Collecte</span>
              </Link>

              <Link
                to="/fatou-ai-reservation-capture-system"
                className={linkClasses('/fatou-ai-reservation-capture-system')}
              >
                <Calendar className="h-4 w-4" />
                <span>Espace Fatou IA</span>
              </Link>

              {/* Clients Pro Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsProClientsMenuOpen(!isProClientsMenuOpen)}
                  className={cn(
                    "flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    location?.pathname?.includes('professional-clients') || location?.pathname?.includes('professional-orders')
                      ? 'text-primary bg-primary/10 dark:bg-primary/20' :'text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-50 dark:hover:bg-slate-700'
                  )}
                >
                  <Building2 className="h-4 w-4" />
                  <span>Clients Pro</span>
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-orange-500 text-white rounded uppercase">PRO</span>
                  <ChevronDown className={cn(
                    "h-4 w-4 transition-transform",
                    isProClientsMenuOpen && "rotate-180"
                  )} />
                </button>

                {isProClientsMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-gray-200 dark:border-slate-700 py-1 z-50">
                    <Link
                      to="/professional-clients-management-dashboard"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsProClientsMenuOpen(false)}
                    >
                      <Building2 className="h-4 w-4" />
                      <span>Liste des clients pro</span>
                    </Link>
                    <Link
                      to="/professional-orders-processing-center"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsProClientsMenuOpen(false)}
                    >
                      <Package className="h-4 w-4" />
                      <span>Commandes pro</span>
                    </Link>
                  </div>
                )}
              </div>

              <Link
                to="/abidjan-container-distribution-dashboard"
                className={linkClasses('/abidjan-container-distribution-dashboard')}
              >
                <Package className="h-4 w-4" />
                <span>Distribution Abidjan</span>
              </Link>

              {/* Gestion Barriques Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsBarrelMenuOpen(!isBarrelMenuOpen)}
                  className={cn(
                    "flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    location?.pathname?.includes('barrel') || location?.pathname?.includes('replenishment') || location?.pathname?.includes('carton') || location?.pathname?.includes('truck-loading')
                      ? 'text-primary bg-primary/10 dark:bg-primary/20' :'text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-50 dark:hover:bg-slate-700'
                  )}
                >
                  <Wine className="h-4 w-4" />
                  <span>Barriques & Cartons</span>
                  <ChevronDown className={cn(
                    "h-4 w-4 transition-transform",
                    isBarrelMenuOpen && "rotate-180"
                  )} />
                </button>

                {isBarrelMenuOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-gray-200 dark:border-slate-700 py-1 z-50">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-slate-700">
                      Ventes & Commandes
                    </div>
                    <Link
                      to="/unified-sales-management"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <ShoppingCart className="h-4 w-4" />
                      <span>Vente Barriques & Cartons</span>
                    </Link>
                    <Link
                      to="/barrel-deposit-scheduling-system"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <Calendar className="h-4 w-4" />
                      <span>Planning Dépôts</span>
                    </Link>
                    <Link
                      to="/barrel-customer-orders-management"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <Package className="h-4 w-4" />
                      <span>Commandes Clients</span>
                    </Link>
                    <Link
                      to="/promo-summer-orders"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <Wine className="h-4 w-4 text-amber-500" />
                      <span className="font-medium text-amber-700 dark:text-amber-400">Commandes Promo Été 2026</span>
                    </Link>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-t border-b border-gray-100 dark:border-slate-700 mt-1">
                      Stocks & Analytiques
                    </div>
                    <Link
                      to="/unified-inventory-management"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <Warehouse className="h-4 w-4" />
                      <span>Inventaire Centralisé</span>
                    </Link>
                    <Link
                      to="/unified-stock-analytics"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>Analytiques Stock</span>
                    </Link>
                    <Link
                      to="/unified-replenishment-center"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <TrendingUp className="h-4 w-4" />
                      <span>Réapprovisionnement</span>
                    </Link>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-t border-gray-100 dark:border-slate-700 mt-1">
                      Fournisseurs & Logistique
                    </div>
                    <Link
                      to="/carton-suppliers-management"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <Truck className="h-4 w-4" />
                      <span>Fournisseurs</span>
                    </Link>
                    <Link
                      to="/partner-accounting"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <TrendingUp className="h-4 w-4 text-blue-500" />
                      <span className="font-medium text-blue-700 dark:text-blue-400">Comptabilité Partenaires</span>
                    </Link>
                    <Link
                      to="/truck-loading-management"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                      onClick={() => setIsBarrelMenuOpen(false)}
                    >
                      <Truck className="h-4 w-4" />
                      <span>Chargement Camions</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Right side with alerts and user menu */}
            <div className="flex items-center space-x-2 md:space-x-3">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Admin Alerts Dropdown - Hidden on mobile, visible on desktop */}
              <div className="hidden md:block">
                <AdminAlertsDropdown />
              </div>

              {/* Administration Menu - Super Admin only */}
              {isSuperAdmin && (
                <div className="relative hidden md:block">
                  <button
                    onClick={() => setIsAdministrationMenuOpen(!isAdministrationMenuOpen)}
                    className={cn(
                      "flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors border",
                      isAdministrationMenuOpen || location?.pathname?.includes('admin-users') || location?.pathname?.includes('security-journal')
                        ? 'bg-purple-600 text-white border-purple-600' :'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-600 hover:text-purple-600 hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-slate-700'
                    )}
                  >
                    <Shield className="h-4 w-4" />
                    <span>Administration</span>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", isAdministrationMenuOpen && "rotate-180")} />
                  </button>
                  {isAdministrationMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 py-1 z-50">
                      <button
                        onClick={() => { navigate('/admin-users-permissions'); setIsAdministrationMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 hover:bg-purple-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-3 text-gray-800 dark:text-gray-300"
                      >
                        <UserCog className="h-4 w-4 text-purple-600" />
                        <div>
                          <div className="font-medium text-sm">Utilisateurs & Permissions</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Gestion des rôles RBAC</div>
                        </div>
                      </button>
                      <button
                        onClick={() => { navigate('/security-journal'); setIsAdministrationMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 hover:bg-purple-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-3 text-gray-800 dark:text-gray-300 border-t border-gray-100 dark:border-slate-700"
                      >
                        <Shield className="h-4 w-4 text-red-600" />
                        <div>
                          <div className="font-medium text-sm">Journal de Sécurité</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Historique des événements</div>
                        </div>
                      </button>
                      <button
                        onClick={() => { navigate('/admin-users-diagnostic'); setIsAdministrationMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 hover:bg-purple-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-3 text-gray-800 dark:text-gray-300 border-t border-gray-100 dark:border-slate-700"
                      >
                        <UserCog className="h-4 w-4 text-orange-600" />
                        <div>
                          <div className="font-medium text-sm">Diagnostic Utilisateurs</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Incohérences Auth ↔ Employés</div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Admin Settings Dropdown Menu - Hidden on mobile, visible on desktop */}
              <div className="relative hidden md:block">
                <button
                  onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border-2 ${
                    isAdminMenuOpen || 
                    location?.pathname === '/n8n-webhook-monitoring-dashboard' || 
                    location?.pathname === '/whats-app-configuration-management' ||
                    location?.pathname === '/fatou-ai-configuration-management' ||
                    location?.pathname === '/fatou-ai-interaction-monitoring-dashboard' ?'bg-green-600 text-white border-green-600 shadow-md' :'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-600 hover:text-green-600 hover:border-green-600 hover:bg-green-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <Settings size={18} />
                  <span>Admin</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${isAdminMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isAdminMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-xl border-2 border-gray-200 dark:border-slate-700 z-50">
                    <button
                      onClick={() => {
                        navigate('/n8n-webhook-monitoring-dashboard');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center ${
                        location?.pathname === '/n8n-webhook-monitoring-dashboard' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <Activity size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Monitoring Webhook</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Logs et statistiques n8n</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/whats-app-configuration-management');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/whats-app-configuration-management' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <MessageSquare size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Configuration WhatsApp</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Gestion des notifications</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/fatou-ai-configuration-management');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/fatou-ai-configuration-management' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <Settings size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Fatou IA Configuration</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Paramètres de l'assistant IA</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/fatou-ai-interaction-monitoring-dashboard');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/fatou-ai-interaction-monitoring-dashboard' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <Activity size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Monitoring Fatou IA</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Surveillance des interactions</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/fatou-ai-knowledge-base-refinement-center');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/fatou-ai-knowledge-base-refinement-center' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <BookOpen size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Refinement Fatou IA</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Amélioration de la base de connaissances</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/fatou-ai-reservation-capture-system');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/fatou-ai-reservation-capture-system' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <Calendar size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Capture Réservations Fatou</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Surveillance temps réel</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/admin-fatou-reservations-dashboard');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/admin-fatou-reservations-dashboard' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <Users size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Dashboard Réservations</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Gestion centralisée</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/seo-management-dashboard');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/seo-management-dashboard' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <TrendingUp size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Dashboard SEO</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Gestion du référencement</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/keyword-research-optimization-center');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/keyword-research-optimization-center' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <Search size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Recherche Mots-clés</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Optimisation SEO</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/technical-seo-performance-analyzer');
                        setIsAdminMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-slate-700 transition-colors flex items-center border-t border-gray-100 dark:border-slate-700 ${
                        location?.pathname === '/technical-seo-performance-analyzer' ? 'bg-green-50 dark:bg-slate-700 text-green-600 font-medium' : 'text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <Activity size={18} className="mr-3" />
                      <div>
                        <div className="font-medium">Analyseur SEO Technique</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Performance & Core Web Vitals</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* User Menu with Logout - Hidden on mobile, visible on desktop */}
              <div className="relative hidden md:block">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border-2 ${
                    isUserMenuOpen
                      ? 'bg-gray-100 border-gray-400 shadow-md'
                      : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-600 hover:border-gray-400 hover:bg-gray-100'
                  }`}
                >
                  <div className="h-7 w-7 rounded-full bg-green-600 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">A</span>
                  </div>
                  <span>Admin</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border-2 border-gray-200 dark:border-slate-700 z-50">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Administrateur</p>
                      <p className="text-xs text-gray-500 mt-0.5">{currentUserEmail || 'Chargement...'}</p>
                    </div>
                    
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-red-50 transition-colors flex items-center text-red-600 font-medium border-b border-gray-200 dark:border-slate-700"
                    >
                      <LogOut size={18} className="mr-3" />
                      <span>Déconnexion</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/shipping-information');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-green-50 transition-colors flex items-center text-green-600 font-medium"
                    >
                      <Package size={18} className="mr-3" />
                      <span>Retour page suivi</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Slide-out menu */}
          <div className="fixed inset-y-0 right-0 w-72 bg-white shadow-xl z-50 md:hidden overflow-y-auto">
            <div className="p-4">
              {/* Close button */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold text-gray-900">Menu</h2>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>

              {/* Navigation items */}
              <div className="space-y-2">
                {mobileNavigationItems?.map((item, index) => {
                  if (item?.isHeader) {
                    return (
                      <div key={index} className="px-4 py-3 text-xs font-bold text-green-600 uppercase tracking-wider border-t border-gray-200 mt-2">
                        {item?.name}
                      </div>
                    );
                  }

                  const Icon = item?.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        item?.onClick?.();
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg transition-colors text-left min-h-[48px] ${
                        item?.isActive
                          ? 'bg-green-50 text-green-600 font-medium' :'text-gray-800 hover:bg-green-50 active:bg-green-100'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item?.name}</span>
                    </button>
                  );
                })}

                {/* Admin menu items */}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <button
                    onClick={() => {
                      navigate('/n8n-webhook-monitoring-dashboard');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg transition-colors text-left min-h-[48px] ${
                      location?.pathname === '/n8n-webhook-monitoring-dashboard' ?'bg-green-50 text-green-600 font-medium' :'text-gray-800 hover:bg-green-50 active:bg-green-100'
                    }`}
                  >
                    <Activity size={20} />
                    <span className="text-base">Monitoring N8N</span>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/whats-app-configuration-management');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg transition-colors text-left min-h-[48px] ${
                      location?.pathname === '/whats-app-configuration-management' ? 'bg-green-50 text-green-600 font-medium' : 'text-gray-800 hover:bg-green-50 active:bg-green-100'
                    }`}
                  >
                    <MessageSquare size={20} />
                    <span className="text-base">Config WhatsApp</span>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/fatou-ai-configuration-management');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg transition-colors text-left min-h-[48px] ${
                      location?.pathname === '/fatou-ai-configuration-management' ? 'bg-green-50 text-green-600 font-medium' :'text-gray-800 hover:bg-green-50 active:bg-green-100'
                    }`}
                  >
                    <Settings size={20} />
                    <span className="text-base">Fatou IA Config</span>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/admin-fatou-reservations-dashboard');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg transition-colors text-left min-h-[48px] ${
                      location?.pathname === '/admin-fatou-reservations-dashboard' ? 'bg-green-50 text-green-600 font-medium' : 'text-gray-800 hover:bg-green-50 active:bg-green-100'
                    }`}
                  >
                    <Bell size={20} />
                    <span className="text-base">Réservations Fatou</span>
                  </button>
                </div>

                {/* Logout */}
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors border-t border-gray-200 dark:border-slate-700 mt-3 pt-4 text-left min-h-[48px]"
                >
                  <LogOut size={20} />
                  <span className="text-base font-medium">Déconnexion</span>
                </button>
                
                {/* Return to tracking page */}
                <button
                  onClick={() => {
                    navigate('/shipping-information');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-green-600 hover:bg-green-50 active:bg-green-100 transition-colors text-left min-h-[48px]"
                >
                  <Package size={20} />
                  <span className="text-base font-medium">Retour page suivi</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}